import { openaiCache } from '../cache-service';
import { performMigration, checkMigrationRequired } from '../cache-migration';
import { readAdminCredentialRevision } from '../storage/admin-credential-revision';
import type { OpenAiUsagePage } from './types';
import type { ApiKeyUsageDetail, ApiResponse, UsageDetailsData, } from './types';
import { getOpenAiMetadata } from './metadata-cache';
import { LIVE_RESOURCE_TTL_MS } from '../provider-resource-cache';
import { PRICING_VERSION } from './pricing';
import { fetchOpenAiUsageWithSegmentCache } from './segment-cache';
import {
  initApiKeyUsageMap,
  createUsageAggregate,
  normalizeUsageBuckets,
  aggregateNormalized,
  applyDailyCostReport,
  finalizeApiKeyMetrics,
  buildDailyUsageArray,
  computeDataWindow,
} from './usage-aggregation';
import { applyProjectWebSearchCosts } from './web-search-cost-allocation';
import { applyProjectGptLiveCosts } from './gpt-live-cost-allocation';
import { debugLog } from '../debug-log';

/**
 * 新しいUsage APIを使用して利用状況詳細データを取得
 */
export const getOrganizationUsageDetails = async (
  apiKey: string,
  onProgress?: (message: string) => void,
  dateRange?: { startDate: Date; endDate: Date; label?: string },
  showUnused?: boolean,
  credentialRevisionOverride?: string,
  options: { forceRefresh?: boolean } = {}
): Promise<ApiResponse<UsageDetailsData>> => {
  const credentialRevision = credentialRevisionOverride ?? readAdminCredentialRevision();
  // Check if migration is required and perform it
  if (checkMigrationRequired()) {
    onProgress?.('Migrating cache to IndexedDB...');
    await performMigration();
  }

  // 日付範囲を計算（デフォルトは過去30日）
  const now = new Date();
  const defaultStartDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const actualDateRange = dateRange || {
    startDate: defaultStartDate,
    endDate: now
  };

  // 日付範囲の差を計算して、適切な精度でキャッシュキーを作成
  const timeDiff = actualDateRange.endDate.getTime() - actualDateRange.startDate.getTime();
  const hoursDiff = timeDiff / (1000 * 60 * 60);
  const isLiveRange = Math.abs(now.getTime() - actualDateRange.endDate.getTime()) < 5 * 60 * 1000;

  // 24時間未満の範囲の場合は、時間単位も含めたキャッシュキーを使用
  const startDate = hoursDiff < 24
    ? actualDateRange.startDate.toISOString() // 完全なISO文字列（時間含む）
    : actualDateRange.startDate.toISOString().split('T')[0]; // 日付のみ
  const endDate = isLiveRange
    ? `live:${actualDateRange.endDate.toISOString().split('T')[0]}`
    : hoursDiff < 24
    ? actualDateRange.endDate.toISOString() // 完全なISO文字列（時間含む）
    : actualDateRange.endDate.toISOString().split('T')[0]; // 日付のみ

  // 1. キャッシュの確認（料金表バージョンもチェック）
  if (options.forceRefresh) {
    await openaiCache.clearOrganizationUsageCache(startDate, endDate, credentialRevision);
  }
  const cachedData = options.forceRefresh ? null : await openaiCache.getOrganizationUsage(
    startDate,
    endDate,
    credentialRevision
  );
  if (cachedData && cachedData.pricingVersion === PRICING_VERSION && cachedData.allApiKeysIncluded === true
    && (!isLiveRange || (cachedData.sourceFreshUntil ?? 0) > Date.now())) {
    debugLog('Returning cached usage data from IndexedDB.');
    // キャッシュされたデータにキャッシュ情報を追加
    const existingCacheTimestamp = cachedData.cacheTimestamp;
    const visibleKeys = showUnused ? cachedData.apiKeyUsage : cachedData.apiKeyUsage.filter((key: ApiKeyUsageDetail) =>
      key.requests > 0 || key.contextTokens > 0 || key.generatedTokens > 0 || key.cachedTokens > 0 || key.cost > 0);
    const dataWithCacheInfo = {
      ...cachedData,
      apiKeyUsage: visibleKeys,
      displayedApiKeys: visibleKeys.length,
      cached: true,
      cacheTimestamp: existingCacheTimestamp ?? Date.now()
    };
    return { success: true, data: dataWithCacheInfo };
  }

  try {
    // 日付範囲をUnix timestampに変換
    const originalStartSeconds = Math.floor(actualDateRange.startDate.getTime() / 1000);
    const originalEndSeconds = Math.floor(actualDateRange.endDate.getTime() / 1000);

    let startTime = originalStartSeconds;
    let endTime = originalEndSeconds;

    debugLog('Date range debug:', {
      startDate: actualDateRange.startDate.toISOString(),
      endDate: actualDateRange.endDate.toISOString(),
      startTime,
      endTime,
      startTimeDate: new Date(startTime * 1000).toISOString(),
      endTimeDate: new Date(endTime * 1000).toISOString()
    });

    onProgress?.(`Fetching data for ${actualDateRange.startDate.toISOString().split('T')[0]} to ${actualDateRange.endDate.toISOString().split('T')[0]}...`);

    const metadataResponse = await getOpenAiMetadata(apiKey, credentialRevision, onProgress, options.forceRefresh);
    if (!metadataResponse.success || !metadataResponse.data) {
      return { success: false, error: metadataResponse.error || 'Failed to fetch metadata' };
    }
    const { projects, allApiKeys, partialErrors: metadataPartialErrors } = metadataResponse.data;
    let sourceFreshUntil = metadataResponse.data.freshUntil;
    onProgress?.(`Found ${allApiKeys.length} API keys`);

    // APIキーのサンプルログ
    if (allApiKeys.length > 0) {
      debugLog('API Keys sample:', allApiKeys.slice(0, 3).map(key => ({
        id: key.id,
        name: key.name,
        projectId: key.projectId
      })));
    }

    // 3. 使用状況データを新しいAPIで取得
    // 日付範囲の差を計算して、適切なbucket_widthを決定
    const timeDiff = actualDateRange.endDate.getTime() - actualDateRange.startDate.getTime();

    const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

    // 期間が24時間を超える場合はレート制限を避けるため日単位で取得
    let bucketWidth: '1h' | '1d' = timeDiff > ONE_DAY_IN_MS ? '1d' : '1h';

    let completionsUsage: ApiResponse<OpenAiUsagePage>;
    let embeddingsUsage: ApiResponse<OpenAiUsagePage>;
    let moderationsUsage: ApiResponse<OpenAiUsagePage>;
    let imagesUsage: ApiResponse<OpenAiUsagePage>;
    let audioSpeechesUsage: ApiResponse<OpenAiUsagePage>;
    let audioTranscriptionsUsage: ApiResponse<OpenAiUsagePage>;
    let costsUsage: ApiResponse<OpenAiUsagePage> | null = null;
    let coverageCompleteForBucketWidth = false;
    let segmentPartialErrors: string[] = [];
    let resolvedEndSeconds = originalEndSeconds;
    let sourceFetchedAt = Date.now();

    const runUsageFetch = async () => {
      startTime = originalStartSeconds;
      endTime = originalEndSeconds;

      if (bucketWidth === '1d') {
        const DAY_SECONDS = 24 * 60 * 60;
        startTime = Math.floor(startTime / DAY_SECONDS) * DAY_SECONDS;
        endTime = Math.ceil(endTime / DAY_SECONDS) * DAY_SECONDS;
      }

      const rangeSeconds = Math.max(0, endTime - startTime);
      const rangeHours = rangeSeconds / (60 * 60);


      debugLog(`🕐 Fetching usage data with bucket_width=${bucketWidth} for ${rangeHours.toFixed(1)} hours range`);
      debugLog('📅 Date range details:', {
        startDate: new Date(startTime * 1000).toISOString(),
        endDate: new Date(endTime * 1000).toISOString(),
        hoursDiff: rangeHours,
        bucketWidth
      });
      onProgress?.('Loading cached OpenAI usage segments...');
      const segmentResult = await fetchOpenAiUsageWithSegmentCache(
        apiKey,
        bucketWidth,
        startTime,
        endTime,
        onProgress,
        credentialRevision,
        options
      );

      completionsUsage = segmentResult.completionsUsage;
      embeddingsUsage = segmentResult.embeddingsUsage;
      moderationsUsage = segmentResult.moderationsUsage;
      imagesUsage = segmentResult.imagesUsage;
      audioSpeechesUsage = segmentResult.audioSpeechesUsage;
      audioTranscriptionsUsage = segmentResult.audioTranscriptionsUsage;
      costsUsage = segmentResult.costsUsage;
      coverageCompleteForBucketWidth = segmentResult.coverageComplete;
      segmentPartialErrors = segmentResult.partialErrors;
      sourceFreshUntil = Math.min(sourceFreshUntil, segmentResult.freshUntil ?? Date.now() + LIVE_RESOURCE_TTL_MS);
      resolvedEndSeconds = segmentResult.dataEndTime ?? originalEndSeconds;
      sourceFetchedAt = segmentResult.fetchedAt ?? Date.now();

      const summarizeBuckets = (label: string, payload: ApiResponse<OpenAiUsagePage>) => {
        const buckets = payload?.data?.data ?? [];
        const firstBucket = buckets[0];
        const lastBucket = buckets[buckets.length - 1];
        debugLog(`[OpenAI Usage] ${label} buckets resolved`, {
          label,
          bucketWidth,
          count: buckets.length,
          firstBucketStart: firstBucket?.start_time ?? firstBucket?.startTime,
          lastBucketStart: lastBucket?.start_time ?? lastBucket?.startTime
        });
      };

      debugLog('[OpenAI Usage] Segment cache summary', {
        bucketWidth,
        reusedSegments: segmentResult.reusedSegments,
        fetchedSegments: segmentResult.fetchedSegments,
        coverageComplete: segmentResult.coverageComplete,
        requestedRange: {
          start: new Date(startTime * 1000).toISOString(),
          end: new Date(endTime * 1000).toISOString()
        }
      });

      summarizeBuckets('Completions', completionsUsage);
      summarizeBuckets('Embeddings', embeddingsUsage);
      summarizeBuckets('Moderations', moderationsUsage);
      summarizeBuckets('Images', imagesUsage);
      summarizeBuckets('Audio Speeches', audioSpeechesUsage);
      summarizeBuckets('Audio Transcriptions', audioTranscriptionsUsage);
      if (costsUsage?.success) {
        summarizeBuckets('Costs', costsUsage);
      }
    };

    let retriedWithHourly = false;
    while (true) {
      await runUsageFetch();
      const usageCounts = [
        completionsUsage?.data?.data?.length ?? 0,
        embeddingsUsage?.data?.data?.length ?? 0,
        moderationsUsage?.data?.data?.length ?? 0,
        imagesUsage?.data?.data?.length ?? 0,
        audioSpeechesUsage?.data?.data?.length ?? 0,
        audioTranscriptionsUsage?.data?.data?.length ?? 0
      ];
      const hasUsageBuckets = usageCounts.some(count => count > 0);

      if (!hasUsageBuckets && bucketWidth === '1d' && !retriedWithHourly && !coverageCompleteForBucketWidth) {
        console.warn('[OpenAI Usage] Daily usage buckets empty, retrying with hourly granularity');
        bucketWidth = '1h';
        retriedWithHourly = true;
        continue;
      }
      break;
    }

    onProgress?.('Processing usage data...');

    // APIキー使用マップを初期化し、集約コンテキストを作成
    debugLog(`Creating usage map for ${allApiKeys.length} API keys`);
    const apiKeyUsageMap = initApiKeyUsageMap(allApiKeys);
    const aggregate = createUsageAggregate(apiKeyUsageMap);
    const fallbackBucketDurationMs = bucketWidth === '1h' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    const normalizeOpts = {
      bucketWidth,
      fallbackBucketDurationMs,
      rangeStart: actualDateRange.startDate,
      rangeEnd: new Date(Math.min(originalEndSeconds, resolvedEndSeconds) * 1000),
    };

    // 4. 各タイプの使用データを正規化して集約
    const usageByType: Array<[ApiResponse<OpenAiUsagePage>, string]> = [
      [completionsUsage, 'completions'],
      [embeddingsUsage, 'embeddings'],
      [moderationsUsage, 'moderations'],
      [imagesUsage, 'images'],
      [audioSpeechesUsage, 'audio_speeches'],
      [audioTranscriptionsUsage, 'audio_transcriptions'],
    ];
    const archivedProjectsById = new Map(
      projects
        .filter(project => project.status === 'archived')
        .map(project => [project.id, project] as const)
    );
    const archivedProjectsWithUnresolvedUsage = new Set<string>();
    for (const [usageResponse, usageType] of usageByType) {
      const normalized = normalizeUsageBuckets(usageResponse, usageType, normalizeOpts);
      for (const event of normalized.events) {
        if (
          event.projectId &&
          archivedProjectsById.has(event.projectId) &&
          (!event.apiKeyId || !apiKeyUsageMap.has(event.apiKeyId))
        ) {
          archivedProjectsWithUnresolvedUsage.add(event.projectId);
        }
      }
      aggregateNormalized(aggregate, normalized);
    }
    const archivedIdentityPartialErrors = Array.from(archivedProjectsWithUnresolvedUsage)
      .sort()
      .map(projectId => {
        const project = archivedProjectsById.get(projectId);
        const projectLabel = project?.name ? `${project.name} (${projectId})` : projectId;
        return `API key owner metadata unavailable for usage from archived project ${projectLabel}`;
      });
    applyProjectGptLiveCosts(aggregate, costsUsage);
    const webSearchAllocation = applyProjectWebSearchCosts(aggregate, costsUsage);
    const pricingPartialErrors = aggregate.unpricedModels.size > 0
      ? [`Pricing unavailable for models with usage: ${Array.from(aggregate.unpricedModels).sort().join(', ')}`]
      : [];
    const allPartialErrors = [
      ...metadataPartialErrors,
      ...archivedIdentityPartialErrors,
      ...segmentPartialErrors,
      ...webSearchAllocation.errors,
      ...pricingPartialErrors
    ];

    // costs API による日次コスト補正（1d のみ）
    if (bucketWidth === '1d') {
      applyDailyCostReport(aggregate, costsUsage);
    }

    // 5. APIキーごとの追加メトリクス
    onProgress?.('Calculating metrics...');
    finalizeApiKeyMetrics(aggregate.apiKeyUsageMap);

    // 使用実績のあるAPIキーのみをフィルタリング（オプション）
    const apiKeyUsageArray = Array.from(aggregate.apiKeyUsageMap.values());
    const filteredApiKeys = showUnused ? apiKeyUsageArray : apiKeyUsageArray.filter(apiKey =>
      apiKey.requests > 0 ||
      apiKey.contextTokens > 0 ||
      apiKey.generatedTokens > 0 ||
      apiKey.cachedTokens > 0 ||
      apiKey.cost > 0
    );

    debugLog(`API keys: ${filteredApiKeys.length} displayed out of ${allApiKeys.length} total (showUnused: ${showUnused})`);

    // Sort daily usage by date and resolve the actual daily API key counts
    const dailyUsage = buildDailyUsageArray(aggregate.dailyUsageMap);

    const dataWindow = computeDataWindow(
      aggregate.earliestBucketStart,
      aggregate.latestBucketEnd,
      { ...actualDateRange, endDate: normalizeOpts.rangeEnd }
    );
    // Providers can return the full end of an in-progress hour/day bucket.
    // The visible watermark must not claim data beyond the snapshot cutoff.
    dataWindow.end = new Date(Math.min(Date.parse(dataWindow.end), normalizeOpts.rangeEnd.getTime())).toISOString();

    const cacheTimestamp = sourceFetchedAt;

    const result: ApiResponse<UsageDetailsData> = {
      success: true,
      data: {
        modelUsage: Array.from(aggregate.modelUsageMap.values()),
        apiKeyUsage: filteredApiKeys,
        totalRequests: aggregate.totals.requests,
        totalCost: aggregate.totals.cost,
        totalContextTokens: aggregate.totals.contextTokens,
        totalGeneratedTokens: aggregate.totals.generatedTokens,
        totalCachedTokens: aggregate.totals.cachedTokens,
        totalApiKeys: allApiKeys.length,
        displayedApiKeys: filteredApiKeys.length,
        dailyUsage,
        projects: projects.map(p => ({
          id: p.id,
          name: p.name,
          status: p.status,
          provider: 'openai' as const
        })),
        dataWindow,
        cacheTimestamp,
        ...(allPartialErrors.length > 0
          ? { partialErrors: allPartialErrors }
          : {})
      }
    };

    // 6. Cache the result — but NOT when it carries partialErrors (e.g. OpenAI
    //    returning 500 for the in-progress UTC day). Caching a degraded/empty
    //    snapshot under the default 24h TTL would keep serving it — and make the
    //    banner's Retry (clearCache=false) a no-op — hiding OpenAI's recovery.
    //    Skipping the write lets the next load re-fetch.
    if (allPartialErrors.length === 0) {
      onProgress?.('Caching results...');
      const dataWithVersion = { ...result.data, apiKeyUsage: apiKeyUsageArray,
        displayedApiKeys: apiKeyUsageArray.length, allApiKeysIncluded: true, pricingVersion: PRICING_VERSION,
        sourceFreshUntil };
      await openaiCache.cacheOrganizationUsage(
        startDate,
        endDate,
        dataWithVersion,
        Math.max(1, sourceFreshUntil - Date.now()),
        credentialRevision
      );
      debugLog('Usage data fetched and cached to IndexedDB using new Usage API.');
    } else {
      console.warn(
        '[OpenAI Usage] Result has partial errors; skipping cache so the next load re-fetches.',
        allPartialErrors
      );
    }
    onProgress?.('Complete!');
    return result;

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch usage details'
    };
  }
};
